import React from 'react';
import {
  RouteObject,
  Route as OriginalRoute,
  matchRoutes as originalMatchRoutes,
  RouteMatch,
  Location,
} from 'react-router';
import { MParticlePageName, PageViewEvent } from './elog/mparticle_types';

// Re-export `RouteObject` with more specific types for route props
type ReverbRouteOverrides = {
  handle?: IRouteSettings;
  index?: false;
};

export type ReverbRouteObject = Omit<
  RouteObject,
  keyof ReverbRouteOverrides
> & ReverbRouteOverrides;

let routes: ReverbRouteObject[];

export function registerRoutes(r: ReverbRouteObject[]) {
  routes = r;
}

// The slice of router state pageViewEvent builders receive. This is what
// trackPageViewForLocation actually constructs -- not a full RouterState --
// so callbacks can only depend on the location and the matched routes.
export interface PageViewRouterState {
  location: Partial<Location>;
  matches: RouteMatch[];
}

type BuildPageViewEvent = (routerState: PageViewRouterState) => PageViewEvent;

// Settings any route may carry on `handle`.
interface BaseRouteHandle {
  // Whether the route renders client-side. Read by core_link to decide between
  // a router navigation and a full page load.
  clientSide?: boolean;
  // The mParticle page view fired on navigation, either a static event or a
  // builder that reads the matched router state.
  pageViewEvent?: PageViewEvent | BuildPageViewEvent;
  // A page name read directly off `handle` by the matched component, for
  // routes that need it outside of page view tracking.
  pageName?: MParticlePageName;
}

// Settings that only CMS routes carry. Set in discovery-ui's cms_route.tsx and
// read in commons' dynamic_page.tsx.
export interface CmsRouteHandle {
  cmsPageType?: string;
  slugOverride?: string;
}

// The full set of custom properties a legacy route may put on `handle`. Adding
// a key here should be rare: prefer an existing setting, and group any new
// feature-specific keys into their own named type the way CmsRouteHandle does.
export type IRouteSettings = BaseRouteHandle & CmsRouteHandle;

// The only shape `lazy` may resolve to. Legacy routes (Core + discovery-ui +
// RRE) exist to code-split a component and nothing else; data loading happens
// in the component via Apollo, not in a route loader. The `never` keys are what
// makes the restriction stick -- object literal freshness is lost through the
// Promise, so an omitted key alone would not be rejected.
interface LazyComponentOnly {
  Component: React.ComponentType<any>;
  loader?: never;
  action?: never;
  middleware?: never;
  shouldRevalidate?: never;
  HydrateFallback?: never;
  hydrateFallbackElement?: never;
  handle?: never;
}

type ReverbLazyRoute = () => Promise<LazyComponentOnly>;

// The props a legacy route may set. Deliberately narrower than react-router's
// RouteProps: `loader`, `action`, `middleware`, `shouldRevalidate` and the
// hydrate fallbacks are absent because legacy routes may not use them.
interface ReverbRouteBaseProps {
  path?: string;
  id?: string;
  caseSensitive?: boolean;
  handle?: IRouteSettings;
  lazy?: ReverbLazyRoute;
  element?: React.ReactNode | null;
  Component?: React.ComponentType | null;
  errorElement?: React.ReactNode | null;
  ErrorBoundary?: React.ComponentType | null;
}

export interface ReverbPathRouteProps extends ReverbRouteBaseProps {
  index?: false;
  children?: React.ReactNode;
}

// Index routes render at their parent's path and cannot nest, mirroring
// react-router's own split between PathRouteProps and IndexRouteProps.
export interface ReverbIndexRouteProps extends ReverbRouteBaseProps {
  index: true;
  children?: never;
}

export type ReverbRouteProps = ReverbPathRouteProps | ReverbIndexRouteProps;

/**
 * react-router's `Route` with narrower props. Import this instead of the
 * react-router one when defining reverb.com routes (an eslint rule in
 * discovery-ui enforces it).
 *
 * This has to stay a cast of the original rather than a wrapping component:
 * `createRoutesFromChildren` asserts `element.type === Route` by reference and
 * throws on anything else.
 */
export const Route = OriginalRoute as (props: ReverbRouteProps) => React.ReactElement | null;

export function matchRoutes(location: Partial<Location>): Promise<RouteMatch[]> {
  return new Promise((resolve) => {
    if (!routes) {
      resolve([]);
      return;
    }

    const matches = originalMatchRoutes(routes, location);
    resolve(matches ?? []);
  });
}

export async function getRouteSettings(location: Partial<Location>): Promise<IRouteSettings[]> {
  const matches = await matchRoutes(location);
  if (!matches) return [];

  // In React Router v7, matchRoutes returns an array of matches with route.handle containing custom props
  return matches
    .map(match => match.route.handle)
    .filter(handle => !!handle);
}
